import React, { useState } from 'react';
import { X, Download, Maximize, Minimize } from 'lucide-react';
import claimAttachmentsService from '../../services/claimAttachmentsService';

const styles = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
    },
    modalContent: {
        position: 'relative',
        backgroundColor: '#fff',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
    },
    modalHeader: {
        padding: '15px 20px',
        borderBottom: '1px solid #e9ecef',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    modalTitle: {
        fontSize: '1.2rem',
        fontWeight: '600',
        margin: 0,
        color: '#0A6EBD',
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    modalBody: {
        padding: '0',
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative'
    },
    previewFrame: {
        width: '100%',
        height: '100%',
        minHeight: '400px',
        border: 'none'
    },
    imagePreview: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain'
    },
    previewError: {
        textAlign: 'center',
        padding: '40px',
        color: '#721c24',
        backgroundColor: '#f8d7da',
        borderRadius: '4px',
        width: '100%'
    },
    modalActions: {
        position: 'absolute',
        top: '15px',
        right: '15px',
        display: 'flex',
        gap: '10px'
    },
    actionButton: {
        background: 'rgba(255, 255, 255, 0.8)',
        border: 'none',
        borderRadius: '50%',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease'
    }
};

const FilePreview = ({
    isOpen,
    onClose,
    attachment,
    trackingNumber
}) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [previewError, setPreviewError] = useState(false);

    if (!isOpen || !attachment) return null;

    if (!trackingNumber || !attachment.id) {
        console.error('Erreur: trackingNumber et attachmentId sont requis pour la prévisualisation');
        return (
            <div style={styles.modalOverlay} onClick={onClose}>
                <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <div style={styles.modalHeader}>
                        <h5 style={styles.modalTitle}>Erreur</h5>
                        <button className="btn btn-sm btn-outline-danger" onClick={onClose}>
                            <X size={18} />
                        </button>
                    </div>
                    <div style={styles.modalBody}>
                        <div style={styles.previewError}>
                            <p>Impossible de prévisualiser le fichier : paramètres manquants.</p>
                            <button className="btn btn-secondary mt-3" onClick={onClose}>Fermer</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const previewUrl = claimAttachmentsService.getPreviewUrl(trackingNumber, attachment.id);
    const downloadUrl = claimAttachmentsService.getDownloadUrl(trackingNumber, attachment.id);
    const isPreviewable = claimAttachmentsService.isPreviewable(attachment.fileName);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleDownload = () => {
        // Créer un lien invisible et cliquer dessus pour télécharger
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = attachment.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePreviewError = () => {
        setPreviewError(true);
    };

    const getFileExtension = (fileName) => {
        return fileName.split('.').pop().toLowerCase();
    };

    const renderPreview = () => {
        if (!isPreviewable) {
            return (
                <div style={styles.previewError}>
                    <p>Le type de fichier "{getFileExtension(attachment.fileName)}" ne peut pas être prévisualisé.</p>
                    <button
                        className="btn btn-primary mt-3"
                        onClick={handleDownload}
                    >
                        <Download size={18} className="me-2" />
                        Télécharger le fichier
                    </button>
                </div>
            );
        }

        if (previewError) {
            return (
                <div style={styles.previewError}>
                    <p>Impossible de prévisualiser ce fichier.</p>
                    <button
                        className="btn btn-primary mt-3"
                        onClick={handleDownload}
                    >
                        <Download size={18} className="me-2" />
                        Télécharger le fichier
                    </button>
                </div>
            );
        }

        // Déterminer le type de prévisualisation selon l'extension
        const extension = getFileExtension(attachment.fileName);
        const isPDF = extension === 'pdf';
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension);

        if (isPDF) {
            return (
                <iframe
                    src={previewUrl}
                    style={styles.previewFrame}
                    title={attachment.fileName}
                    onError={handlePreviewError}
                />
            );
        }

        if (isImage) {
            return (
                <img
                    src={previewUrl}
                    alt={attachment.fileName}
                    style={styles.imagePreview}
                    onError={handlePreviewError}
                />
            );
        }

        return null;
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div
                style={{
                    ...styles.modalContent,
                    ...(isFullscreen ? { width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', borderRadius: 0 } : {})
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={styles.modalHeader}>
                    <h5 style={styles.modalTitle}>{attachment.fileName}</h5>
                    <div>
                        <button
                            className="btn btn-sm btn-outline-secondary me-2"
                            onClick={toggleFullscreen}
                            title={isFullscreen ? "Quitter le mode plein écran" : "Mode plein écran"}
                        >
                            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
                        </button>
                        <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={handleDownload}
                            title="Télécharger"
                        >
                            <Download size={18} />
                        </button>
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={onClose}
                            title="Fermer"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div style={styles.modalBody}>
                    {renderPreview()}
                </div>
            </div>
        </div>
    );
};

export default FilePreview;